import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, Clock, Shield, Key, Wrench, DoorOpen, Award, Zap, FileText, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { companyInfo, services, getSubServiceById } from '../../data/mock';

const iconMap = {
  glass: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  ),
  key: Key,
  door: DoorOpen,
  clock: Clock,
  shield: Shield,
  wrench: Wrench,
  award: Award,
  zap: Zap,
  'file-text': FileText,
  'check-circle': CheckCircle
};

const ServicesPage = () => {
  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">Services</li>
          </ol>
        </nav>

        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#262626] mb-4">
            Nos Services à Vitry-sur-Seine
          </h1>
          <p className="text-lg text-[#666666] max-w-2xl mx-auto">
            Solutions complètes en vitrerie, serrurerie et menuiserie à Vitry-sur-Seine 
            et dans tout le Val-de-Marne (94)
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || CheckCircle;
            const isUrgent = service.id === 'urgence';
            return (
              <Card key={service.id} className={`overflow-hidden ${
                isUrgent ? 'border-[#B91C1C]/30 bg-[#B91C1C]/5' : ''
              }`}>
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#B91C1C]/10">
                      <IconComponent className="w-8 h-8 text-[#B91C1C]" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-[#262626] mb-2">{service.title}</h2>
                      <p className="text-[#666666]">{service.shortDesc}</p>
                    </div>
                  </div>
                  
                  {/* Sous-services */}
                  {service.subServices && service.subServices.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-[#262626] mb-3 uppercase tracking-wide">Prestations</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {service.subServices.slice(0, 6).map((sub) => (
                          <Link 
                            key={sub.id} 
                            to={`/services/${service.id}/${sub.id}`}
                            className="flex items-center gap-2 text-sm text-[#666666] hover:text-[#B91C1C] transition-colors group"
                          >
                            <ChevronRight className="w-4 h-4 text-[#B91C1C] group-hover:translate-x-1 transition-transform" />
                            {sub.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Link to={`/services/${service.id}`}>
                    <Button className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                      Voir tous les services
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-[#B91C1C] rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Besoin d'un devis à Vitry-sur-Seine ?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Contactez-nous pour une évaluation gratuite de vos besoins
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
              <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-gray-100 text-[#B91C1C]">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#B91C1C]">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold text-[#262626]">Service non trouvé</h1>
        <Link to="/services" className="text-[#B91C1C] mt-4 inline-block">Retour aux services</Link>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || CheckCircle;
  const isUrgent = service.id === 'urgence';

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li><Link to="/services" className="hover:text-[#B91C1C]">Services</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">{service.title}</li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center bg-[#B91C1C]/10">
                <IconComponent className="w-8 h-8 text-[#B91C1C]" />
              </div>
              <div>
                {isUrgent && (
                  <span className="inline-block bg-[#B91C1C] text-white text-xs px-2 py-1 rounded mb-1">
                    Disponible 24h/24
                  </span>
                )}
                <h1 className="text-3xl md:text-4xl font-bold text-[#262626]">
                  {service.title} à Vitry-sur-Seine
                </h1>
              </div>
            </div>

            <p className="text-lg text-[#666666] mb-8">{service.description}</p>

            {/* Sous-services détaillés */}
            {service.subServices && service.subServices.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-[#262626] mb-6">Nos prestations</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.subServices.map((sub) => (
                    <Link key={sub.id} to={`/services/${service.id}/${sub.id}`}>
                      <Card className="h-full hover:shadow-md transition-all hover:-translate-y-1 hover:border-[#B91C1C]/30 cursor-pointer">
                        <CardContent className="p-5">
                          <h3 className="font-bold text-[#262626] mb-2">{sub.title}</h3>
                          <p className="text-sm text-[#666666] mb-3">{sub.shortDesc}</p>
                          <div className="flex items-center text-sm text-[#B91C1C] font-medium">
                            En savoir plus
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <h2 className="text-2xl font-bold text-[#262626] mb-4">Pourquoi nous choisir ?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#B91C1C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="w-4 h-4 text-[#B91C1C]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#262626]">Intervention rapide à Vitry-sur-Seine</h3>
                  <p className="text-[#666666]">Sur place en 30 minutes dans tout le Val-de-Marne</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#B91C1C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-4 h-4 text-[#B91C1C]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#262626]">20+ ans d'expérience</h3>
                  <p className="text-[#666666]">Artisans qualifiés et expérimentés</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#B91C1C]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-[#B91C1C]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#262626]">Devis gratuit et transparent</h3>
                  <p className="text-[#666666]">Pas de surprise, prix convenu avant intervention</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-[#B91C1C]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#262626] mb-4">Besoin d'aide à Vitry ?</h3>
                <p className="text-[#666666] mb-6">
                  Contactez-nous pour un devis gratuit ou une intervention rapide
                </p>
                <div className="space-y-3">
                  <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                    <Button className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                      <Phone className="w-4 h-4 mr-2" />
                      {companyInfo.phoneEmergency}
                    </Button>
                  </a>
                  <Link to="/contact" className="block">
                    <Button variant="outline" className="w-full border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                      Demander un devis
                    </Button>
                  </Link>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-[#666666] mb-2">Horaires</p>
                  <p className="text-[#262626] font-medium">{companyInfo.openingHours.weekdays}</p>
                  <p className="text-[#B91C1C] font-medium">{companyInfo.openingHours.emergency}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// TEMPLATE RÉUTILISABLE POUR LES SOUS-SERVICES
const SubServicePage = () => {
  const { serviceId, subServiceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  const subService = getSubServiceById(serviceId, subServiceId);

  if (!service || !subService) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold text-[#262626]">Service non trouvé</h1>
        <Link to="/services" className="text-[#B91C1C] mt-4 inline-block">Retour aux services</Link>
      </div>
    );
  }

  // Autres sous-services du même parent pour le maillage interne
  const relatedSubServices = service.subServices?.filter(s => s.id !== subServiceId).slice(0, 4) || [];
  
  // Autres services principaux pour le maillage
  const otherServices = services.filter(s => s.id !== serviceId).slice(0, 3);

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb SEO */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center flex-wrap gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li><Link to="/services" className="hover:text-[#B91C1C]">Services</Link></li>
            <li>/</li>
            <li><Link to={`/services/${serviceId}`} className="hover:text-[#B91C1C]">{service.title}</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">{subService.title}</li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Badge Urgence */}
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#B91C1C] text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Urgence 24h/24 à Vitry-sur-Seine
              </span>
            </div>

            {/* H1 SEO Optimisé */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#262626] mb-4">
              {subService.title} à Vitry-sur-Seine (94)
            </h1>
            
            <p className="text-xl text-[#666666] mb-8">
              {subService.description}
            </p>

            {/* CTA prominent */}
            <div className="bg-[#B91C1C] rounded-xl p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-white">
                <p className="font-bold text-lg">Besoin d'un {subService.title.toLowerCase()} ?</p>
                <p className="text-white/80">Intervention rapide en 30 minutes</p>
              </div>
              <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                <Button size="lg" className="bg-white hover:bg-gray-100 text-[#B91C1C] font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phoneEmergency}
                </Button>
              </a>
            </div>

            {/* Features / Prestations */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#262626] mb-6">
                Nos prestations {subService.title.toLowerCase()} à Vitry-sur-Seine
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {subService.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-[#B91C1C]/30 transition-colors">
                    <CheckCircle className="w-5 h-5 text-[#B91C1C] flex-shrink-0" />
                    <span className="text-[#262626]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contenu SEO */}
            <div className="prose prose-lg max-w-none mb-10">
              <h2 className="text-2xl font-bold text-[#262626] mb-4">
                Votre spécialiste {subService.title.toLowerCase()} à Vitry-sur-Seine
              </h2>
              <p className="text-[#666666] mb-4">
                EMS Express est votre artisan de confiance pour tous vos besoins en <strong>{subService.title.toLowerCase()}</strong> à 
                <strong> Vitry-sur-Seine</strong> et dans tout le Val-de-Marne (94). Avec plus de 20 ans d'expérience, 
                notre équipe intervient rapidement pour vous offrir un service de qualité.
              </p>
              <p className="text-[#666666] mb-4">
                Que vous soyez un particulier ou un professionnel, nous vous proposons des solutions adaptées à vos besoins. 
                Notre intervention est rapide (30 minutes en moyenne) et notre devis est toujours gratuit et transparent.
              </p>
              <p className="text-[#666666]">
                Disponibles <strong>24h/24 et 7j/7</strong> pour les urgences, nous intervenons également à 
                Ivry-sur-Seine, Créteil, Villejuif et dans toutes les communes du 94.
              </p>
            </div>

            {/* Maillage interne - Autres prestations du même service */}
            {relatedSubServices.length > 0 && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-[#262626] mb-6">
                  Autres services {service.title.toLowerCase()}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {relatedSubServices.map((sub) => (
                    <Link key={sub.id} to={`/services/${serviceId}/${sub.id}`}>
                      <Card className="h-full hover:shadow-md transition-all hover:-translate-y-1 hover:border-[#B91C1C]/30">
                        <CardContent className="p-4">
                          <h3 className="font-bold text-[#262626] mb-1">{sub.title}</h3>
                          <p className="text-sm text-[#666666] mb-2 line-clamp-2">{sub.shortDesc}</p>
                          <span className="text-sm text-[#B91C1C] font-medium flex items-center">
                            En savoir plus <ArrowRight className="w-3 h-3 ml-1" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Maillage interne - Autres services principaux */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-[#262626] mb-6">Nos autres services</h2>
              <div className="flex flex-wrap gap-3">
                {otherServices.map((svc) => (
                  <Link 
                    key={svc.id} 
                    to={`/services/${svc.id}`}
                    className="px-4 py-2 bg-white border rounded-lg hover:border-[#B91C1C] hover:bg-[#B91C1C]/5 transition-colors text-[#262626]"
                  >
                    {svc.title}
                  </Link>
                ))}
                <Link 
                  to="/services"
                  className="px-4 py-2 bg-[#B91C1C] text-white rounded-lg hover:bg-[#991B1B] transition-colors"
                >
                  Tous nos services
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* CTA Card */}
              <Card className="border-[#B91C1C]">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#262626] mb-4">
                    Devis gratuit {subService.title.toLowerCase()}
                  </h3>
                  <p className="text-[#666666] mb-6">
                    Intervention rapide à Vitry-sur-Seine et Val-de-Marne
                  </p>
                  <div className="space-y-3">
                    <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                      <Button className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                        <Phone className="w-4 h-4 mr-2" />
                        {companyInfo.phoneEmergency}
                      </Button>
                    </a>
                    <Link to="/contact" className="block">
                      <Button variant="outline" className="w-full border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                        Demander un devis
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Horaires */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-[#262626] mb-4">Horaires d'intervention</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#666666]">Lun - Sam</span>
                      <span className="text-[#262626] font-medium">{companyInfo.openingHours.weekdays}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#666666]">Urgences</span>
                      <span className="text-[#B91C1C] font-medium">{companyInfo.openingHours.emergency}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Zone intervention */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-[#262626] mb-4">Zone d'intervention</h3>
                  <p className="text-sm text-[#666666] mb-3">
                    Nous intervenons à Vitry-sur-Seine et dans tout le Val-de-Marne :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Vitry-sur-Seine', 'Ivry', 'Créteil', 'Villejuif', 'Maisons-Alfort'].map((city) => (
                      <span key={city} className="text-xs bg-gray-100 text-[#262626] px-2 py-1 rounded">
                        {city}
                      </span>
                    ))}
                  </div>
                  <Link to="/zones" className="text-sm text-[#B91C1C] font-medium mt-3 inline-flex items-center">
                    Voir toutes les zones <ArrowRight className="w-3 h-3 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ServicesPage, ServiceDetailPage, SubServicePage };